import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import Reveal from '@/components/Reveal';
import './HomePage.css';

interface Highlight {
  title: string;
  desc: string;
  cta: string;
}

const HIGHLIGHT_PATHS = ['/about', '/solutions', '/impact', '/ecosystem'];

export default function HomePage() {
  const { t } = useTranslation();
  const compliance = t('hero.compliance', { returnObjects: true }) as string[];
  const highlights = t('home.highlights', { returnObjects: true }) as Highlight[];

  return (
    <>
      {/* Hero */}
      <section className="hero-shell section-full">
        <div className="section-inner">
          <h1 className="hero-h1">{t('hero.h1')}</h1>
          <p className="hero-sub">{t('hero.sub')}</p>
          <div className="hero-cta">
            <Link className="btn btn-primary" to="/solutions">
              {t('hero.ctaExplore')}
            </Link>
            <Link className="btn btn-ghost" to="/contact">
              {t('hero.ctaTalk')}
            </Link>
          </div>
          <div className="hero-trust">
            <span className="hero-trust-label">
              <ShieldCheck size={16} aria-hidden="true" />
              {t('hero.trustLabel')}
            </span>
            <span className="hero-trust-sep" aria-hidden="true" />
            <ul className="hero-trust-badges">
              {compliance.map((c) => (
                <li key={c} className="hero-trust-badge">
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What we do — highlights linking to each page */}
      <section className="chapter chapter--tint home-highlights">
        <div className="chapter-inner">
          <div className="chapter-head chapter-head--center">
            <p className="section-kicker">{t('home.highlightsKicker')}</p>
            <h2 className="chapter-title">{t('home.highlightsTitle')}</h2>
            <p className="chapter-lead">{t('home.highlightsLead')}</p>
          </div>
          <div className="highlights-grid">
            {highlights.map((h, i) => (
              <Reveal key={h.title} delay={(i % 2) * 0.08}>
                <Link to={HIGHLIGHT_PATHS[i]} className="card highlight-card">
                  <span className="highlight-index">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="highlight-title">{h.title}</h3>
                  <p className="highlight-desc">{h.desc}</p>
                  <span className="highlight-cta">
                    {h.cta}
                    <ArrowRight size={16} className="highlight-arrow" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="chapter home-cta">
        <div className="home-cta-inner">
          <h2 className="home-cta-title">{t('ctaBand.title')}</h2>
          <p className="home-cta-lead">{t('ctaBand.lead')}</p>
          <Link className="btn btn-primary" to="/contact">
            {t('ctaBand.button')}
          </Link>
        </div>
      </section>
    </>
  );
}
